import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppFab from "@/components/WhatsAppFab";
import WelcomeModal from "@/components/WelcomeModal";
import QuickRechargeButton from "@/components/QuickRechargeButton";
import { useAuth } from "@/lib/auth";
import { api, formatEUR } from "@/lib/api";
import { Plus, FileText, Wallet, Crown, Trash, ArrowRight, ChartBar, Sparkle, Lightning, Warning, Files, Bank, Receipt, FilePdf } from "@phosphor-icons/react";

export default function Dashboard() {
    const { user } = useAuth();
    const [params] = useSearchParams();
    const wantsMaster = params.get("master") === "1";
    const [analyses, setAnalyses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creditAlerts, setCreditAlerts] = useState([]);
    const [dictamenes, setDictamenes] = useState([]);
    const [pdfLoading, setPdfLoading] = useState(null);

    async function load() {
        try {
            const { data } = await api.get("/analyses");
            setAnalyses(data.items || []);
        } finally {
            setLoading(false);
        }
    }
    async function loadAlerts() {
        try {
            const { data } = await api.get("/user/credits-summary");
            setCreditAlerts(data?.alerts || []);
        } catch { /* silent */ }
    }
    async function loadDictamenes() {
        try {
            const { data } = await api.get("/dictamenes");
            setDictamenes(data?.items || []);
        } catch { /* silent */ }
    }
    async function downloadDictamenPdf(d) {
        setPdfLoading(d.id);
        try {
            const r = await api.get(`/dictamenes/${d.id}/pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `Dictamen_${(d.empresa || "dictamen").replace(/[^\w]+/g, "_")}_${d.id.slice(0, 8)}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch { alert("No se pudo descargar el PDF del dictamen."); } finally { setPdfLoading(null); }
    }
    useEffect(() => { load(); loadAlerts(); loadDictamenes(); }, []);

    async function handleDelete(id) {
        if (!confirm("¿Eliminar este análisis?")) return;
        await api.delete(`/analyses/${id}`);
        load();
    }

    const planLabel = { free: "Gratis", pro: "Pro", pro_yearly: "Pro Anual", empresa: "Asesoría", empresa_yearly: "Asesoría Anual" }[user?.plan] || "Gratis";

    // Créditos unificados (€)
    const credits = Number(user?.credits_eur || 0);
    const plan = user?.plan || "free";
    const isYearly = plan === "pro_yearly" || plan === "empresa_yearly";
    const planLimits = {
        pro: { monthly: 3, label: "Pro" },
        empresa: { monthly: 6, label: "Asesoría" },
        pro_yearly: { yearly: 36, label: "Pro Anual" },
        empresa_yearly: { yearly: 72, label: "Asesoría Anual" },
    };
    const planCfg = planLimits[plan];
    const reportsUsed = isYearly ? (user?.reports_this_year || 0) : (user?.reports_this_month || 0);
    const reportsLimit = isYearly ? (planCfg?.yearly || 0) : (planCfg?.monthly || 0);
    const balancesBasicoDisp = Math.floor(credits / 24);
    const balancesMasterDisp = Math.floor(credits / 44);
    const planExpires = user?.plan_expires_at ? new Date(user.plan_expires_at) : null;
    const daysToExpire = planExpires ? Math.ceil((planExpires - new Date()) / (1000 * 60 * 60 * 24)) : null;
    const lowBalance = credits < 24 && credits > 0;
    const noCredits = credits < 24;

    return (
        <div className="min-h-screen flex flex-col">
            <Navbar />
            <main className="flex-1 max-w-7xl mx-auto w-full px-6 md:px-12 py-12">
                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between gap-6 mb-10 pb-10 border-b border-[var(--gf-border)]">
                    <div>
                        <div className="micro-label mb-3">— Panel de control</div>
                        <h1 className="serif text-4xl md:text-5xl">Hola, {user?.name?.split(" ")[0] || "amigo"}.</h1>
                        <p className="text-[var(--gf-text-secondary)] mt-2">Elige por dónde empezar hoy.</p>
                    </div>
                </div>

                {/* Créditos alertas (< 20€) */}
                {creditAlerts.length > 0 && (
                    <div className="mb-8 space-y-3" data-testid="credit-alerts-block">
                        {creditAlerts.map((a) => (
                            <div
                                key={a.credit_kind}
                                className={`flex items-start gap-4 p-5 border-l-4 ${
                                    a.severity === "critical"
                                        ? "bg-red-50 border-l-red-500"
                                        : "bg-amber-50 border-l-amber-500"
                                }`}
                                data-testid={`credit-alert-${a.credit_kind}`}
                            >
                                <Warning size={26} weight="fill" className={`flex-shrink-0 mt-0.5 ${a.severity === "critical" ? "text-red-600" : "text-amber-600"}`} />
                                <div className="flex-1 min-w-0">
                                    <div className={`font-semibold ${a.severity === "critical" ? "text-red-800" : "text-amber-800"}`}>
                                        Créditos bajos · {formatEUR(a.current)} disponibles
                                    </div>
                                    <div className="text-sm mt-1 text-[var(--gf-text-secondary)]" data-testid={`credit-alert-msg-${a.credit_kind}`}>
                                        {a.message || "Recarga antes de agotar tus créditos para no interrumpir el servicio."}
                                    </div>
                                </div>
                                <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
                                    <QuickRechargeButton
                                        amount={a.severity === "critical" ? 14 : 24}
                                        nextPath="/dashboard"
                                        className="btn-gold text-xs whitespace-nowrap"
                                        testId={`quick-recharge-${a.credit_kind}`}
                                    />
                                    <Link to="/pagos" className="btn-outline text-xs whitespace-nowrap justify-center" data-testid={`renew-btn-${a.credit_kind}`}>
                                        Otras recargas <ArrowRight size={14} />
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Tres accesos principales */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12" data-testid="dual-access">
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                        <Link
                            to="/analisis/nuevo"
                            className="group block h-full border border-[var(--gf-border)] bg-white p-8 hover:border-[var(--gf-gold)] transition-all"
                            data-testid="access-balances"
                        >
                            <div className="flex items-start justify-between mb-6">
                                <div className="w-14 h-14 bg-[var(--gf-navy)]/5 flex items-center justify-center">
                                    <Bank size={28} weight="light" className="text-[var(--gf-navy)]" />
                                </div>
                                <ArrowRight size={20} className="text-[var(--gf-text-muted)] group-hover:text-[var(--gf-gold)] group-hover:translate-x-1 transition-all" />
                            </div>
                            <div className="micro-label mb-3">— Análisis de balances</div>
                            <h2 className="serif text-2xl md:text-3xl mb-3 text-[var(--gf-navy)]">Interpretación de balances</h2>
                            <p className="text-sm text-[var(--gf-text-secondary)] leading-relaxed mb-4">
                                Sube un balance, ratios automáticos, diagnóstico bancario e informe interpretativo con IA.
                            </p>
                            <div className="flex flex-wrap gap-2 text-xs text-[var(--gf-text-muted)]">
                                <span className="border border-[var(--gf-border)] px-2 py-0.5">Básico 24€</span>
                                <span className="border border-[var(--gf-border)] px-2 py-0.5">Master 44€</span>
                            </div>
                        </Link>
                    </motion.div>

                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                        <Link
                            to="/portal-asesor"
                            className="group block h-full border border-[var(--gf-border)] bg-white p-8 hover:border-[var(--gf-gold)] transition-all"
                            data-testid="access-portal-asesor"
                        >
                            <div className="flex items-start justify-between mb-6">
                                <div className="w-14 h-14 bg-[var(--gf-gold)]/10 flex items-center justify-center">
                                    <Files size={28} weight="light" className="text-[var(--gf-gold)]" />
                                </div>
                                <ArrowRight size={20} className="text-[var(--gf-text-muted)] group-hover:text-[var(--gf-gold)] group-hover:translate-x-1 transition-all" />
                            </div>
                            <div className="micro-label mb-3">— Digitalización</div>
                            <h2 className="serif text-2xl md:text-3xl mb-3 text-[var(--gf-navy)]">Digitalización y libros de IVA</h2>
                            <p className="text-sm text-[var(--gf-text-secondary)] leading-relaxed mb-4">
                                Sube un ZIP de facturas, la IA extrae los datos y genera los libros de IVA en Excel listos para Hacienda.
                            </p>
                            <div className="flex flex-wrap gap-2 text-xs text-[var(--gf-text-muted)]">
                                <span className="border border-[var(--gf-border)] px-2 py-0.5">OCR Vision</span>
                                <span className="border border-[var(--gf-border)] px-2 py-0.5">Emitidas + Recibidas</span>
                                <span className="border border-[var(--gf-border)] px-2 py-0.5">Multi-empresa</span>
                            </div>
                        </Link>
                    </motion.div>

                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                        <Link
                            to="/verifactu"
                            className="group block h-full border border-[var(--gf-border)] bg-white p-8 hover:border-[var(--gf-gold)] transition-all"
                            data-testid="access-verifactu"
                        >
                            <div className="flex items-start justify-between mb-6">
                                <div className="w-14 h-14 bg-[var(--gf-gold)]/10 flex items-center justify-center">
                                    <Receipt size={28} weight="light" className="text-[var(--gf-gold)]" />
                                </div>
                                <ArrowRight size={20} className="text-[var(--gf-text-muted)] group-hover:text-[var(--gf-gold)] group-hover:translate-x-1 transition-all" />
                            </div>
                            <div className="micro-label mb-3">— Facturación</div>
                            <h2 className="serif text-2xl md:text-3xl mb-3 text-[var(--gf-navy)]">Facturación Verifactu</h2>
                            <p className="text-sm text-[var(--gf-text-secondary)] leading-relaxed mb-4">
                                Emite facturas conforme al RD 1007/2023 · Numeración correlativa, huella encadenada y QR de comprobación AEAT.
                            </p>
                            <div className="flex flex-wrap gap-2 text-xs text-[var(--gf-text-muted)]">
                                <span className="border border-[var(--gf-border)] px-2 py-0.5">RD 1007/2023</span>
                                <span className="border border-[var(--gf-border)] px-2 py-0.5">QR + Huella</span>
                                <span className="border border-[var(--gf-border)] px-2 py-0.5">Multi-empresa</span>
                            </div>
                        </Link>
                    </motion.div>
                </div>

                {/* Banner de aviso si saldo bajo */}
                {noCredits && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
                        className="mb-8 p-5 border-l-4 border-l-[var(--gf-gold)] bg-[var(--gf-gold)]/10 flex items-start gap-4"
                        data-testid="balance-warning-banner"
                    >
                        <Warning size={24} weight="fill" className="text-[var(--gf-gold)] flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                            <div className="font-semibold mb-1">
                                {credits > 0 ? "Créditos bajos" : "Sin créditos disponibles"}
                            </div>
                            <div className="text-sm text-[var(--gf-text-secondary)]">
                                Necesitas al menos 24 € para generar un Balance Básico. Recarga tus créditos y consúmelos donde quieras: balances, OCR, modificaciones o Verifactu.
                            </div>
                        </div>
                        <Link to="/pagos" className="btn-gold whitespace-nowrap" data-testid="banner-cta-pagos">
                            Recargar créditos <ArrowRight size={14} />
                        </Link>
                    </motion.div>
                )}

                {/* Stats cards */}
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                    {/* Plan activo */}
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
                        <div className="gf-card-ficha h-full" data-testid="stat-plan">
                            <div className="flex items-center justify-between mb-4">
                                <Crown size={24} className="text-[var(--gf-gold)]" weight="light" />
                                <span className="micro-label">Plan activo</span>
                            </div>
                            <div className="editorial-num leading-none" style={{ fontSize: "2rem" }}>{planLabel}</div>
                            {planCfg && reportsLimit > 0 ? (
                                <div className="mt-3">
                                    <div className="flex items-center justify-between text-xs mb-1">
                                        <span className="text-[var(--gf-text-secondary)]">{isYearly ? "Informes este año" : "Informes este mes"}</span>
                                        <span className="mono font-semibold">{reportsUsed}/{reportsLimit}</span>
                                    </div>
                                    <div className="h-1.5 bg-[var(--gf-border)] overflow-hidden">
                                        <div className="h-full bg-[var(--gf-gold)] transition-all" style={{ width: `${Math.min(100, (reportsUsed / reportsLimit) * 100)}%` }} />
                                    </div>
                                    {daysToExpire !== null && daysToExpire > 0 && (
                                        <div className="text-[11px] text-[var(--gf-text-muted)] mt-2">
                                            Renueva en {daysToExpire} día{daysToExpire !== 1 ? "s" : ""}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="mt-3">
                                    <Link to="/pagos" className="text-sm text-[var(--gf-gold)] hover:underline inline-block" data-testid="cta-upgrade-plan">
                                        Contratar plan →
                                    </Link>
                                </div>
                            )}
                        </div>
                    </motion.div>

                    {/* Créditos disponibles */}
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                        <div className="gf-card-ficha h-full" data-testid="stat-bono">
                            <div className="flex items-center justify-between mb-4">
                                <Wallet size={24} className="text-[var(--gf-gold)]" weight="light" />
                                <span className="micro-label">Créditos</span>
                            </div>
                            <div className="editorial-num mono leading-none text-[var(--gf-gold)]" style={{ fontSize: "2rem" }}>{formatEUR(credits)}</div>
                            <div className="text-xs text-[var(--gf-text-secondary)] mt-3 space-y-0.5">
                                <div className="text-[var(--gf-text-muted)]">Consumibles en cualquier módulo</div>
                                <Link to="/pagos" className="text-[var(--gf-gold)] hover:underline inline-block mt-1" data-testid="cta-recharge-bono">
                                    Recargar →
                                </Link>
                            </div>
                        </div>
                    </motion.div>

                    {/* Informes disponibles según créditos */}
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
                        <div className="gf-card-ficha h-full" data-testid="stat-master">
                            <div className="flex items-center justify-between mb-4">
                                <Sparkle size={24} className="text-[var(--gf-gold)]" weight="light" />
                                <span className="micro-label">Informes disponibles</span>
                            </div>
                            <div className="flex items-baseline gap-4 mt-2">
                                <div>
                                    <div className="editorial-num mono leading-none text-[var(--gf-navy)]" style={{ fontSize: "2rem" }} data-testid="stat-basico-value">{balancesBasicoDisp}</div>
                                    <div className="text-[10px] uppercase tracking-widest text-[var(--gf-text-muted)] mt-1">Básico · 24€</div>
                                </div>
                                <div>
                                    <div className="editorial-num mono leading-none text-[var(--gf-gold)]" style={{ fontSize: "2rem" }} data-testid="stat-master-value">{balancesMasterDisp}</div>
                                    <div className="text-[10px] uppercase tracking-widest text-[var(--gf-text-muted)] mt-1">Master · 44€</div>
                                </div>
                            </div>
                            <div className="text-xs text-[var(--gf-text-secondary)] mt-3">
                                Estimación en función de tus créditos actuales.
                                <Link to="/pagos" className="text-[var(--gf-gold)] hover:underline inline-block mt-1 ml-1" data-testid="cta-buy-master">
                                    Recargar →
                                </Link>
                            </div>
                        </div>
                    </motion.div>

                    {/* Análisis realizados */}
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                        <div className="gf-card-ficha h-full" data-testid="stat-analyses">
                            <div className="flex items-center justify-between mb-4">
                                <ChartBar size={24} className="text-[var(--gf-gold)]" weight="light" />
                                <span className="micro-label">Análisis realizados</span>
                            </div>
                            <div className="editorial-num mono leading-none" style={{ fontSize: "2rem" }}>{analyses.length}</div>
                            <div className="text-xs text-[var(--gf-text-secondary)] mt-3">
                                {analyses.length === 0
                                    ? <span className="text-[var(--gf-text-muted)]">Aún ninguno</span>
                                    : <span>En total · {analyses.filter(a => a.is_master).length} Master</span>}
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* Analyses list */}
                <div className="gf-card">
                    {wantsMaster && (
                        <div className="mb-6 border-l-4 border-l-[var(--gf-gold)] bg-[var(--gf-gold)]/10 p-4 text-sm flex items-start gap-3" data-testid="master-picker-banner">
                            <Crown size={20} weight="fill" className="text-[var(--gf-gold)] flex-shrink-0 mt-0.5" />
                            <div>
                                <strong className="text-[var(--gf-navy)]">Informe Master · 44 créditos.</strong>{" "}
                                El Master se genera sobre un Informe Financiero previo: elige abajo el balance que quieres ampliar y pulsa "Ver" para generar el Master desde su informe.
                                {analyses.length === 0 && <> Aún no tienes ninguno — <Link to="/analisis/nuevo" className="underline text-[var(--gf-navy)]">crea primero un Informe Financiero</Link>.</>}
                            </div>
                        </div>
                    )}
                    <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                        <div>
                            <div className="micro-label mb-2">— Historial de balances</div>
                            <h2 className="serif text-2xl">Tus análisis</h2>
                        </div>
                        <Link to="/analisis/nuevo" className="btn-gold" data-testid="new-analysis-btn">
                            <Plus size={16} weight="bold" /> Nuevo análisis
                        </Link>
                    </div>

                    {loading ? (
                        <div className="text-center py-16 text-[var(--gf-text-secondary)]">Cargando...</div>
                    ) : analyses.length === 0 ? (
                        <div className="text-center py-16" data-testid="empty-analyses">
                            <FileText size={48} className="mx-auto mb-4 text-[var(--gf-text-muted)]" weight="light" />
                            <p className="text-[var(--gf-text-secondary)] mb-4">Aún no has analizado ningún balance.</p>
                            <Link to="/analisis/nuevo" className="btn-gold inline-flex">
                                <Plus size={16} weight="bold" /> Crear mi primer análisis
                            </Link>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="data-table" data-testid="analyses-table">
                                <thead>
                                    <tr>
                                        <th>Empresa</th>
                                        <th>Periodo</th>
                                        <th>Fecha</th>
                                        <th className="text-right">Activos</th>
                                        <th className="text-right">Resultado</th>
                                        <th className="text-right">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {analyses.map((a) => (
                                        <tr key={a.id} data-testid={`analysis-row-${a.id}`}>
                                            <td className="font-medium">{a.company_name}</td>
                                            <td className="mono text-sm">{a.period}</td>
                                            <td className="text-sm text-[var(--gf-text-secondary)]">
                                                {new Date(a.created_at).toLocaleDateString("es-ES")}
                                            </td>
                                            <td className="text-right mono text-sm">
                                                {formatEUR(a.ratios?.totales?.activos_totales || 0)}
                                            </td>
                                            <td className="text-right mono text-sm">
                                                {formatEUR(a.balance?.resultado || 0)}
                                            </td>
                                            <td className="text-right">
                                                <div className="flex justify-end gap-2">
                                                    <Link to={`/analisis/${a.id}`} className="text-[var(--gf-navy)] hover:text-[var(--gf-gold)] inline-flex items-center gap-1 text-sm" data-testid={`view-${a.id}`}>
                                                        Ver <ArrowRight size={14} />
                                                    </Link>
                                                    <button onClick={() => handleDelete(a.id)} className="text-[var(--gf-text-muted)] hover:text-[var(--gf-danger)] ml-3" data-testid={`delete-${a.id}`} aria-label="Eliminar">
                                                        <Trash size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Historial de dictámenes pre-bancarios */}
                <div className="gf-card mt-8" data-testid="dictamenes-card">
                    <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                        <div>
                            <div className="micro-label mb-2">— Historial de dictámenes</div>
                            <h2 className="serif text-2xl">Dictámenes Pre-Bancarios</h2>
                            <p className="text-xs text-[var(--gf-text-secondary)] mt-1">Reabre cualquier dictamen ya pagado sin volver a consumir créditos. Los recálculos sobre él siguen siendo gratis.</p>
                        </div>
                        <Link to="/maquina-verdad" className="btn-outline text-sm" data-testid="new-dictamen-btn">
                            <Lightning size={14} weight="bold" /> Nuevo dictamen · 14 créditos
                        </Link>
                    </div>
                    {dictamenes.length === 0 ? (
                        <div className="text-center py-10 text-sm text-[var(--gf-text-secondary)]" data-testid="empty-dictamenes">
                            Aún no has emitido ningún Dictamen Pre-Bancario.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="data-table" data-testid="dictamenes-table">
                                <thead>
                                    <tr>
                                        <th>Empresa</th>
                                        <th>Fecha</th>
                                        <th>Veredicto</th>
                                        <th className="text-right">Ratios</th>
                                        <th className="text-right">Coste</th>
                                        <th className="text-right">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {dictamenes.map((d) => (
                                        <tr key={d.id} data-testid={`dictamen-row-${d.id}`}>
                                            <td className="font-medium">{d.empresa || <span className="text-[var(--gf-text-muted)] italic">Sin nombre</span>}</td>
                                            <td className="text-sm text-[var(--gf-text-secondary)]">{new Date(d.created_at).toLocaleDateString("es-ES")}</td>
                                            <td><VeredictoBadge color={d.veredicto} /></td>
                                            <td className="text-right mono text-xs">
                                                <span className="text-emerald-700">{d.verdes_ratios ?? 0}</span> · <span className="text-amber-700">{d.ambars ?? 0}</span> · <span className="text-red-700">{d.rojos ?? 0}</span>
                                            </td>
                                            <td className="text-right mono text-sm">{d.charged_eur > 0 ? formatEUR(d.charged_eur) : "—"}</td>
                                            <td className="text-right">
                                                <div className="flex justify-end items-center gap-3">
                                                    <button onClick={() => downloadDictamenPdf(d)} disabled={pdfLoading === d.id} className="text-[var(--gf-text-secondary)] hover:text-[var(--gf-navy)] inline-flex items-center gap-1 text-sm disabled:opacity-50" title="Descargar PDF" data-testid={`pdf-${d.id}`}>
                                                        <FilePdf size={16} weight="light" /> {pdfLoading === d.id ? "…" : "PDF"}
                                                    </button>
                                                    <Link to={`/maquina-verdad?dictamen=${d.id}`} className="text-[var(--gf-navy)] hover:text-[var(--gf-gold)] inline-flex items-center gap-1 text-sm" data-testid={`reopen-${d.id}`}>
                                                        Reabrir <ArrowRight size={14} />
                                                    </Link>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </main>
            <WelcomeModal />
            <Footer />
            <WhatsAppFab />
        </div>
    );
}

function VeredictoBadge({ color }) {
    const map = {
        verde: ["bg-emerald-50 text-emerald-700 border-emerald-200", "Apto"],
        ambar: ["bg-amber-50 text-amber-700 border-amber-200", "Condicionado"],
        rojo: ["bg-red-50 text-red-700 border-red-200", "No apto"],
    };
    const [cls, label] = map[color] || ["bg-gray-50 text-gray-600 border-gray-200", "—"];
    return <span className={`inline-block border px-2 py-0.5 text-[10px] uppercase tracking-widest font-semibold ${cls}`} data-testid="veredicto-badge">{label}</span>;
}
